import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, X, CalendarDays, Clock, MapPin, Phone, Mail, ShieldCheck, ClipboardList, Users, CheckCircle2 } from "lucide-react";
import { ADMIN } from "@/constants/testIds";
import { PhotoGallery } from "@/components/PhotoUpload";

const STATUS_MAP = {
  pending: { label: "In attesa", cls: "bg-[#FFF4E5] text-[#7A5211] border-[#F5A623]" },
  approved: { label: "Confermata", cls: "bg-primary/10 text-primary border-primary/30" },
  rejected: { label: "Rifiutata", cls: "bg-destructive/10 text-destructive border-destructive/30" },
  completed: { label: "Completata", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  cancelled: { label: "Annullata", cls: "bg-foreground/5 text-foreground/60 border-border" },
};
const SVC_LABEL = { sopralluogo: "Sopralluogo", showroom: "Show room", riparazione: "Riparazione" };

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, completed: 0, users: 0 });
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null); // { booking, action }
  const [note, setNote] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login"); return; }
    if (user.role !== "admin") { nav("/"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const load = async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([api.get("/bookings"), api.get("/admin/stats")]);
      setItems(b.data);
      setStats(s.data);
    } catch (e) { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  };

  const doAction = async () => {
    if (!dlg) return;
    const { booking, action } = dlg;
    try {
      await api.patch(`/bookings/${booking.booking_id}/status`, { status: action, admin_notes: note || null });
      toast.success("Aggiornato");
      setDlg(null); setNote("");
      load();
    } catch (e) { toast.error("Errore aggiornamento"); }
  };

  const pending = items.filter((b) => b.status === "pending");
  const all = items;

  return (
    <div className="max-w-7xl mx-auto px-5 py-10 md:py-14">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-5 w-5 text-primary" />
        <div className="text-xs tracking-widest uppercase text-primary">Dashboard admin</div>
      </div>
      <h1 className="font-display text-3xl md:text-5xl mt-2">Gestione prenotazioni</h1>

      {/* stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-8">
        <StatCard label="Totale" value={stats.total} testId={ADMIN.statsTotal} icon={<ClipboardList className="h-4 w-4"/>} />
        <StatCard label="In attesa" value={stats.pending} testId={ADMIN.statsPending} highlight icon={<Clock className="h-4 w-4"/>} />
        <StatCard label="Confermate" value={stats.approved} icon={<Check className="h-4 w-4"/>} />
        <StatCard label="Completate" value={stats.completed} icon={<CheckCircle2 className="h-4 w-4"/>} />
        <StatCard label="Clienti" value={stats.users} icon={<Users className="h-4 w-4"/>} />
      </div>

      <Tabs defaultValue="pending" className="mt-10">
        <TabsList>
          <TabsTrigger value="pending" data-testid={ADMIN.tabPending}>In attesa ({pending.length})</TabsTrigger>
          <TabsTrigger value="all" data-testid={ADMIN.tabAll}>Tutte ({all.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="mt-6"><BookingList items={pending} loading={loading} onAct={(b,a)=>{ setDlg({booking:b, action:a}); setNote(""); }} /></TabsContent>
        <TabsContent value="all" className="mt-6"><BookingList items={all} loading={loading} onAct={(b,a)=>{ setDlg({booking:b, action:a}); setNote(""); }} /></TabsContent>
      </Tabs>

      <Dialog open={!!dlg} onOpenChange={(o)=>!o && setDlg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dlg?.action === "approved" && "Approvare la richiesta?"}
              {dlg?.action === "rejected" && "Rifiutare la richiesta?"}
              {dlg?.action === "completed" && "Segnare come completata?"}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-foreground/70">
            {dlg?.booking && (
              <div className="rounded-md bg-secondary p-3 mb-3">
                <b>{SVC_LABEL[dlg.booking.service_type]}</b> · {dlg.booking.date} {dlg.booking.time_slot}<br/>
                {dlg.booking.user_name} ({dlg.booking.user_email})
              </div>
            )}
            <label className="text-sm font-medium">Note per il cliente (facoltativo)</label>
            <Textarea rows={3} value={note} onChange={(e)=>setNote(e.target.value)} className="mt-1" placeholder="Es. Confermiamo l'appuntamento. Portare planimetria." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setDlg(null)}>Annulla</Button>
            <Button onClick={doAction} className={dlg?.action === "rejected" ? "bg-destructive hover:bg-destructive/90" : ""}>Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, icon, highlight, testId }) {
  return (
    <div className={`bg-white border ${highlight ? "border-[#F5A623]/60" : "border-border"} rounded-xl p-5 card-lift`} data-testid={testId}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-foreground/60">{icon} {label}</div>
      <div className="font-display text-3xl mt-2">{value}</div>
    </div>
  );
}

function BookingList({ items, loading, onAct }) {
  if (loading) return <div className="text-foreground/60">Caricamento…</div>;
  if (items.length === 0) return <div className="bg-white border border-border rounded-xl p-10 text-center text-foreground/60">Nessuna prenotazione.</div>;
  return (
    <div className="space-y-3">
      {items.map((b) => <BookingRow key={b.booking_id} b={b} onAct={onAct} />)}
    </div>
  );
}

function BookingRow({ b, onAct }) {
  const st = STATUS_MAP[b.status];
  const [photos, setPhotos] = useState([]);
  useEffect(() => {
    if (b.service_type === "riparazione") {
      api.get(`/bookings/${b.booking_id}/photos`).then(r => setPhotos(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.booking_id]);
  return (
    <div data-testid={ADMIN.row(b.booking_id)} className="bg-white border border-border rounded-xl p-5 card-lift">
      <div className="flex flex-col md:flex-row md:items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="font-display text-lg">{SVC_LABEL[b.service_type]}</div>
            <Badge className={`border ${st.cls}`}>{st.label}</Badge>
            <span className="font-mono text-xs text-foreground/50">#{b.booking_id.slice(-6)}</span>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-foreground/70 mt-2">
            <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4"/> {b.date}</span>
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4"/> {b.time_slot}</span>
            <span className="flex items-center gap-1.5"><Mail className="h-4 w-4"/> {b.user_email}</span>
            <span className="flex items-center gap-1.5"><Phone className="h-4 w-4"/> {b.user_phone}</span>
            {b.address && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4"/> {b.address}</span>}
          </div>
          <div className="text-sm mt-2 text-foreground/80"><b>{b.user_name}</b></div>
          {b.repair_type && <div className="text-sm mt-1"><span className="text-foreground/60">Tipo:</span> {b.repair_type}</div>}
          {b.description && <div className="text-sm mt-1 italic text-foreground/70">"{b.description}"</div>}
          {photos.length > 0 && <PhotoGallery bookingId={b.booking_id} photos={photos} />}
          {b.admin_notes && <div className="text-sm mt-2 p-2 rounded-md bg-secondary"><b>Note:</b> {b.admin_notes}</div>}
        </div>
        <div className="flex flex-wrap gap-2">
          {b.status === "pending" && (
            <>
              <Button size="sm" onClick={()=>onAct(b, "approved")} data-testid={ADMIN.approveBtn(b.booking_id)} className="rounded-full">
                <Check className="h-4 w-4 mr-1"/> Approva
              </Button>
              <Button size="sm" variant="outline" onClick={()=>onAct(b, "rejected")} data-testid={ADMIN.rejectBtn(b.booking_id)} className="rounded-full text-destructive border-destructive/30 hover:bg-destructive/5">
                <X className="h-4 w-4 mr-1"/> Rifiuta
              </Button>
            </>
          )}
          {b.status === "approved" && (
            <Button size="sm" variant="outline" onClick={()=>onAct(b, "completed")} data-testid={ADMIN.completeBtn(b.booking_id)} className="rounded-full">
              <CheckCircle2 className="h-4 w-4 mr-1"/> Completa
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
